import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Info, ChevronLeft, ChevronRight, Search, X, Lock, Plus, Check, Library } from 'lucide-react';
import { SkipControls, canSkip } from '../components/SkipControls';
import { useSaved } from '../lib/useSaved';
import { useAuth } from '../context/AuthContext';
import { resolvePlayable, pickSource, clipLabel, clipName } from '../lib/media';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#c8a672';
// Home-page tile widths.
const TILE_W = 'w-[240px] sm:w-[340px]';
const TILE_W_PORTRAIT = 'w-[140px] sm:w-[200px]';
const BONE = '#f5efe6';

// Deep-black canvas from the brand kit. Netflix uses pure black; ours has a
// hint of warm brown so the saddle-gold accents don't feel neon.
const BG = '#0a0705';

// ---- helpers ---------------------------------------------------------------
const DISCIPLINE_ORDER = [
  { key: 'heading', label: 'Head & Heel · Heading' },
  { key: 'heeling', label: 'Head & Heel · Heeling' },
  { key: 'breakaway', label: 'Breakaway' },
  { key: 'tie-down', label: 'Tie-Down' },
  { key: 'barrel-racing', label: 'Barrel Racing' },
  { key: 'ranch-roping', label: 'Ranch Roping' },
  { key: 'general', label: 'Ranch Life' },
];

// Accent color per discipline. Powers the branded placeholder tile so an
// empty poster still tells the viewer which discipline the video belongs to.
const DISCIPLINE_ACCENT = {
  heading:        { hue: '#a11d1d', label: 'Heading' },
  heeling:        { hue: '#c26a1d', label: 'Heeling' },
  breakaway:      { hue: '#6b2fa1', label: 'Breakaway' },
  'tie-down':     { hue: '#1d5aa1', label: 'Tie-Down' },
  'barrel-racing':{ hue: '#0d7377', label: 'Barrel Racing' },
  'ranch-roping': { hue: '#5b3a1d', label: 'Ranch Roping' },
  general:        { hue: '#3a3a3a', label: 'Ranch Life' },
  event:          { hue: '#8a6b1d', label: 'Event' },
  coach:          { hue: '#5b3a1d', label: 'Coach' },
};
const LOGO_MARK = 'https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png';

// Return the record's thumbnail (already derived server-side for library
// videos + past events). Never returns a broken URL.
const fallbackThumb = (v) => v?.thumbnail_url || null;

// Test-data filter — hides obvious QA rows on the demo page. Keeps real
// records that just happen to lack a thumbnail. Applied to library + coaches
// + events before rendering rails / search groups.
const TEST_PATTERN = /^(test[_ ]|testing[_ ]|demo[_ ]|qa[_ ])/i;
const isTestRecord = (r) => {
  if (!r) return false;
  const title = (r.title || r.name || '').trim();
  if (!title) return true; // empty title = incomplete row, skip
  return TEST_PATTERN.test(title);
};

// Touch-device detection — hover peek only makes sense on devices with a
// mouse. On phones/tablets tapping a tile just navigates.
const useIsHoverDevice = () => {
  const [isHover, setIsHover] = useState(true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setIsHover(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);
  return isHover;
};

const RowArrows = ({ scroll }) => (
  <>
    <button
      type="button"
      aria-label="Scroll left"
      className="hidden md:flex absolute left-0 top-0 bottom-0 z-10 w-12 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-black/80 to-transparent"
      onClick={() => scroll(-1)}
      data-testid="netflix-row-left"
    >
      <ChevronLeft className="w-8 h-8 text-white" />
    </button>
    <button
      type="button"
      aria-label="Scroll right"
      className="hidden md:flex absolute right-0 top-0 bottom-0 z-10 w-12 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-l from-black/80 to-transparent"
      onClick={() => scroll(1)}
      data-testid="netflix-row-right"
    >
      <ChevronRight className="w-8 h-8 text-white" />
    </button>
  </>
);

// ---- rail primitive --------------------------------------------------------
// A horizontal row of tiles. Tiles are rendered by the caller via `renderTile`.
const Rail = ({ title, items, renderTile, testid, elevated = false }) => {
  const scrollerRef = useRef(null);
  if (!items || items.length === 0) return null;

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section
      className={`relative group py-6 ${elevated ? 'my-2 border-y' : ''}`}
      style={elevated ? { borderColor: 'rgba(200,166,114,0.35)', background: 'linear-gradient(180deg, rgba(200,166,114,0.08), rgba(10,7,5,0))' } : undefined}
      data-testid={testid}
    >
      {elevated && (
        <div className="px-4 sm:px-8 text-[10px] font-mono uppercase tracking-[0.35em] mb-1" style={{ color: GOLD }} data-testid={`${testid}-elevated-badge`}>
          Elevated Picks
        </div>
      )}
      <h2
        className="font-display uppercase text-lg sm:text-xl mb-3 px-4 sm:px-8 tracking-wide"
        style={{ color: elevated ? GOLD : BONE }}
      >
        {title}
      </h2>
      <div className="relative">
        <RowArrows scroll={scroll} />
        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide px-4 sm:px-8 pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map((item, i) => renderTile(item, i))}
        </div>
      </div>
    </section>
  );
};

// ---- video modal -----------------------------------------------------------
// Fullscreen dark overlay with an embedded video player (Vimeo/YouTube iframe).
// Portalled to document.body so it escapes any parent stacking context.
// Closes on backdrop-click, X button, or Escape.
export const VideoModal = ({ open, onClose, title, embedCode, videoId, videoKind, saved = false, onToggleSave = null }) => {
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => { setLoadFailed(false); }, [embedCode, open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // One-shot view counter: fires the first time Vimeo reports a `play` event
  // (NOT just when the modal opens). Uses postMessage like Vimeo's lightweight
  // JS SDK — we send `addEventListener:play`, then listen for events named
  // `play`. Each modal mount counts as at most one view.
  useEffect(() => {
    if (!open || !videoId || !videoKind) return;
    const iframe = document.querySelector('[data-testid="netflix-video-iframe"]');
    if (!iframe) return;
    let counted = false;
    const subscribe = () => {
      try {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), '*');
      } catch (_) { /* cross-origin — handled by onLoad timing */ }
    };
    const onMsg = (ev) => {
      if (!ev.data) return;
      let data = ev.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if ((data.event === 'play' || data.method === 'play') && !counted) {
        counted = true;
        axios.post(`${API_URL}/api/videos/view`, { video_id: videoId, kind: videoKind }).catch(() => {});
      }
    };
    // Subscribe once the iframe reports ready (via onload) and also on
    // mount — Vimeo accepts duplicate subscriptions silently.
    iframe.addEventListener('load', subscribe);
    subscribe();
    window.addEventListener('message', onMsg);
    return () => {
      window.removeEventListener('message', onMsg);
      iframe.removeEventListener('load', subscribe);
    };
  }, [open, videoId, videoKind]);

  if (!open || typeof window === 'undefined') return null;

  const playable = resolvePlayable(embedCode);
  const getTarget = () => document.querySelector('[data-testid="netflix-video-iframe"], [data-testid="netflix-video-native"]');

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-3 bg-black/95 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="netflix-video-modal"
    >
      <button
        type="button"
        aria-label="Close player"
        onClick={onClose}
        className="absolute top-4 right-4 z-20 w-11 h-11 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors"
        data-testid="netflix-video-close"
      >
        <X className="w-6 h-6" />
      </button>
      <div
        className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="absolute top-0 inset-x-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
            <div className="font-display uppercase text-white text-lg tracking-wide" data-testid="netflix-video-title">{title}</div>
          </div>
        )}
        {playable.kind === 'iframe' && (
          <iframe
            src={playable.src}
            title={title || 'Video player'}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="netflix-video-iframe"
          />
        )}
        {playable.kind === 'video' && (
          <video
            src={playable.src}
            controls
            autoPlay
            playsInline
            preload="auto"
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            className="absolute inset-0 w-full h-full bg-black"
            onError={() => setLoadFailed(true)}
            data-testid="netflix-video-native"
          />
        )}
        {playable.kind === 'video' && loadFailed && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black text-white/70 text-sm text-center px-6" data-testid="netflix-video-load-failed">
            This video file couldn&apos;t be loaded — the file link may have expired or been removed. Please contact an admin.
          </div>
        )}
        {playable.kind === 'none' && (
          <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm text-center px-6" data-testid="netflix-video-no-source">
            This clip doesn&apos;t have a playable source yet. Please contact an admin.
          </div>
        )}
      </div>
      {playable.kind !== 'none' && (
        <div className="flex flex-wrap items-center justify-center gap-3" onClick={(e) => e.stopPropagation()}>
          {canSkip(playable.kind, playable.src) && <SkipControls getTarget={getTarget} testidPrefix="netflix-skip" />}
          {onToggleSave && (
            <button
              type="button"
              onClick={onToggleSave}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
              style={saved ? { background: GOLD, color: '#0a0705', borderColor: GOLD } : { color: BONE, borderColor: 'rgba(255,255,255,0.25)' }}
              data-testid="netflix-video-save"
            >
              {saved ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {saved ? 'In My List' : 'My List'}
            </button>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
};

// ---- login prompt modal ----------------------------------------------------
export const LoginPromptModal = ({ open, onClose, title, mode = 'login', onSubscribe }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof window === 'undefined') return null;

  const needsSub = mode === 'subscribe';
  const heading = needsSub ? 'Subscribers only' : 'Sign in to watch';
  const copyWithTitle = needsSub
    ? <>&ldquo;{title}&rdquo; is reserved for active Elevated members. Start a subscription to hit play.</>
    : <>&ldquo;{title}&rdquo; is available to Elevated members. Sign in or start a subscription to hit play.</>;
  const copyNoTitle = needsSub
    ? <>This clip is reserved for active Elevated members. Start a subscription to hit play.</>
    : <>This clip is available to Elevated members. Sign in or start a subscription to hit play.</>;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="netflix-login-prompt"
    >
      <div
        className="relative w-full max-w-md rounded-xl overflow-hidden bg-[#141010] border border-[#c8a672]/40 p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white/80"
          data-testid="netflix-login-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
          <Lock className="w-6 h-6" />
        </div>
        <h3 className="font-display uppercase text-2xl mb-2" style={{ color: BONE }}>
          {heading}
        </h3>
        <p className="text-sm text-white/70 mb-6">
          {title ? copyWithTitle : copyNoTitle}
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => { onClose(); onSubscribe && onSubscribe(); }}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-[1.02]"
            style={{ background: GOLD, color: '#0a0705' }}
            data-testid="netflix-login-prompt-subscribe"
          >
            Start a Subscription
          </button>
          {!needsSub && (
            <Link
              to="/login"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-md font-semibold transition-all border border-white/30 text-white hover:bg-white/5"
              data-testid="netflix-login-prompt-signin"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ---- subscribe plan modal --------------------------------------------------
// Netflix-themed plan picker. Hits the SAME /api/payments/checkout endpoint
// the legacy /subscription page uses, so Stripe webhooks keep writing
// subscription_status, stripe_customer_id and credits exactly like before.
// Unauthenticated viewers see a Netflix-styled signup step FIRST so they
// create an account inline before being redirected to Stripe — nobody ever
// hits the old /signup page from this flow.
export const SubscribePlanModal = ({ open, onClose, isAuthenticated }) => {
  const { signup } = useAuth();
  const [plans, setPlans] = useState(null);
  const [step, setStep] = useState('plans'); // 'plans' | 'signup'
  const [chosenPlan, setChosenPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState('');
  // Signup form state — mirrors the legacy /signup fields (name, email,
  // phone, password) so the server-side validation (phone >= 10 digits,
  // unique email) matches.
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [signingUp, setSigningUp] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setStep('plans');
    setChosenPlan(null);
    setLoadingPlan(null);
    axios.get(`${API_URL}/api/subscriptions/plans`)
      .then((r) => setPlans(r.data?.plans || {}))
      .catch(() => setPlans({
        monthly: { price: 25.00, name: 'Monthly', period: 'month' },
        annual: { price: 240.00, name: 'Annual', period: 'year' },
      }));
  }, [open]);

  if (!open || typeof window === 'undefined') return null;

  // Shared "call /payments/checkout then redirect to Stripe" helper. Reused
  // after an inline signup so new accounts flow straight into Stripe without
  // the user having to click "Choose Plan" a second time.
  const callCheckout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (resp.data?.url) {
        window.location.href = resp.data.url;
      } else {
        setError('Could not start checkout. Please try again.');
        setLoadingPlan(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed. Please try again.');
      setLoadingPlan(null);
    }
  };

  const choosePlan = async (planId) => {
    setError('');
    if (!isAuthenticated) {
      // Unauth → show the inline Netflix signup form. Only after the user
      // creates an account do we call /payments/checkout and redirect to
      // Stripe. This way we never leave the Netflix experience for the
      // old /signup page.
      setChosenPlan(planId);
      setStep('signup');
      return;
    }
    await callCheckout(planId);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const digits = (form.phone || '').replace(/\D/g, '');
    if (!form.name || !form.email || digits.length < 10 || !form.password) {
      setError('Please fill in every field. Phone needs at least 10 digits.');
      return;
    }
    if (form.password.length < 6) {
      setError('Please use a password with at least 6 characters.');
      return;
    }
    setSigningUp(true);
    try {
      await signup(form.email, form.password, form.name, form.phone);
      // Account exists & we're logged in — fire straight into Stripe for
      // the plan the user picked a moment ago. The webhook will mark them
      // active on return.
      await callCheckout(chosenPlan);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create account. Please try again.');
    } finally {
      setSigningUp(false);
    }
  };

  const planList = plans ? Object.entries(plans).map(([id, p]) => ({ id, ...p })) : [];
  const features = [
    'Every live event from the arena',
    'Full on-demand library + replays',
    'Monthly credits for critiques',
    'Discounted event photos & videos',
    'TV / AirPlay pairing',
  ];
  const chosenPlanObj = plans && chosenPlan ? { id: chosenPlan, ...plans[chosenPlan] } : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="netflix-subscribe-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden bg-[#141010] border border-[#c8a672]/40 p-6 sm:p-10 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white/80 z-10"
          data-testid="netflix-subscribe-close"
        >
          <X className="w-4 h-4" />
        </button>

        {step === 'plans' && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
                <Lock className="w-6 h-6" />
              </div>
              <h3 className="font-display uppercase text-3xl mb-2" style={{ color: BONE }}>
                Pick your plan
              </h3>
              <p className="text-sm text-white/70 max-w-md mx-auto">
                Full access to live events, replays, photo &amp; video coverage, and monthly credits. Cancel any time.
              </p>
            </div>

            {!plans ? (
              <div className="py-10 text-center text-white/60 text-sm">Loading plans…</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {planList.map((plan) => {
                  const popular = plan.id === 'annual';
                  return (
                    <div
                      key={plan.id}
                      className={`relative rounded-xl p-5 border transition-all ${popular ? 'bg-[#1a1210]' : 'bg-black/40'}`}
                      style={{ borderColor: popular ? GOLD : 'rgba(255,255,255,0.1)' }}
                      data-testid={`netflix-plan-${plan.id}`}
                    >
                      {popular && (
                        <div
                          className="absolute -top-3 right-4 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                          style={{ background: GOLD, color: '#0a0705' }}
                        >
                          Save 20%
                        </div>
                      )}
                      <div className="font-display uppercase text-xl mb-1" style={{ color: BONE }}>
                        {plan.name}
                      </div>
                      <div className="flex items-baseline gap-1 mb-4">
                        <span className="font-display font-black text-4xl" style={{ color: BONE }}>
                          ${Math.round(plan.price)}
                        </span>
                        <span className="text-sm text-white/60">/{plan.period}</span>
                      </div>
                      <ul className="space-y-1.5 mb-5 text-sm text-white/80">
                        {features.map((f) => (
                          <li key={f} className="flex items-start gap-2">
                            <span className="mt-0.5" style={{ color: GOLD }}>✓</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => choosePlan(plan.id)}
                        disabled={loadingPlan !== null}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                        style={{ background: popular ? GOLD : BONE, color: '#0a0705' }}
                        data-testid={`netflix-plan-${plan.id}-cta`}
                      >
                        {loadingPlan === plan.id ? 'Redirecting to Stripe…' : (isAuthenticated ? `Choose ${plan.name}` : `Choose ${plan.name}`)}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {error && (
              <div className="mt-4 text-sm text-red-300 text-center" data-testid="netflix-subscribe-error">
                {error}
              </div>
            )}

            {!isAuthenticated && (
              <p className="mt-6 text-center text-xs text-white/50">
                Already a member? <Link to="/login" className="underline hover:text-white">Sign in instead</Link>
              </p>
            )}
          </>
        )}

        {step === 'signup' && (
          <>
            <button
              type="button"
              onClick={() => { setStep('plans'); setError(''); }}
              className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1"
              data-testid="netflix-signup-back"
            >
              ← Change plan
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
                <Lock className="w-6 h-6" />
              </div>
              <h3 className="font-display uppercase text-3xl mb-2" style={{ color: BONE }}>
                Create your account
              </h3>
              <p className="text-sm text-white/70 max-w-md mx-auto">
                {chosenPlanObj
                  ? <>You picked the <span style={{ color: GOLD }}>{chosenPlanObj.name}</span> plan — ${Math.round(chosenPlanObj.price)}/{chosenPlanObj.period}. Next stop is Stripe for payment.</>
                  : 'Set up your Elevated login. Next stop is Stripe for payment.'}
              </p>
            </div>
            <form onSubmit={handleSignupSubmit} className="space-y-3 max-w-md mx-auto" data-testid="netflix-signup-form">
              <input
                type="text"
                required
                autoComplete="name"
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-md bg-black/60 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#c8a672]"
                data-testid="netflix-signup-name"
              />
              <input
                type="email"
                required
                autoComplete="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-md bg-black/60 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#c8a672]"
                data-testid="netflix-signup-email"
              />
              <input
                type="tel"
                required
                autoComplete="tel"
                placeholder="Mobile phone (for critique updates)"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="w-full px-4 py-3 rounded-md bg-black/60 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#c8a672]"
                data-testid="netflix-signup-phone"
              />
              <input
                type="password"
                required
                autoComplete="new-password"
                placeholder="Password (6+ characters)"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-4 py-3 rounded-md bg-black/60 border border-white/15 text-white placeholder-white/40 focus:outline-none focus:border-[#c8a672]"
                data-testid="netflix-signup-password"
              />
              {error && (
                <div className="text-sm text-red-300 text-center" data-testid="netflix-signup-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={signingUp || loadingPlan !== null}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[48px] rounded-md font-semibold transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                style={{ background: GOLD, color: '#0a0705' }}
                data-testid="netflix-signup-submit"
              >
                {signingUp
                  ? 'Creating your account…'
                  : loadingPlan
                  ? 'Redirecting to Stripe…'
                  : 'Create Account & Continue to Payment'}
              </button>
              <p className="text-center text-[11px] text-white/40">
                By continuing you agree to our Terms &amp; Privacy. Payments are processed securely by Stripe.
              </p>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

// ---- info modal ------------------------------------------------------------
// Netflix-style detail sheet for non-video tiles (coaches, upcoming events).
// Keeps the viewer on /netflix — no navigation to the old site.
const InfoModal = ({ open, onClose, record }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !record || typeof window === 'undefined') return null;
  const {
    title, image, subtitle, description, meta = [], portrait, kind = 'video',
    discipline = 'general', primaryCta,
  } = record;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="netflix-info-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-xl overflow-hidden bg-[#141010] border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-black/60 hover:bg-black/80 text-white border border-white/20"
          data-testid="netflix-info-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className={portrait ? 'aspect-[3/2] bg-black relative' : 'aspect-video bg-black relative'}>
          {image ? (
            <img src={image} alt={title} className="w-full h-full object-cover" />
          ) : (
            <PlaceholderPoster discipline={discipline} kind={kind} />
          )}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#141010] to-transparent" />
          <div className="absolute left-6 bottom-4 right-16">
            <h3 className="font-display uppercase text-2xl sm:text-3xl leading-tight" style={{ color: BONE }}>
              {title}
            </h3>
            {subtitle && (
              <div className="text-xs uppercase tracking-widest text-white/70 mt-1">{subtitle}</div>
            )}
          </div>
        </div>
        <div className="p-6 space-y-4">
          {description && (
            <p className="text-sm sm:text-base text-white/80 leading-relaxed whitespace-pre-line">
              {description}
            </p>
          )}
          {meta.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {meta.map((m) => (
                <span key={m} className="text-[10px] uppercase tracking-widest text-white/70 border border-white/20 rounded-sm px-2 py-1">
                  {m}
                </span>
              ))}
            </div>
          )}
          {primaryCta && (
            <button
              type="button"
              onClick={primaryCta.onClick}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-[1.02]"
              style={{ background: GOLD, color: '#0a0705' }}
              data-testid="netflix-info-cta"
            >
              {primaryCta.label}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};




// ---- placeholder poster ----------------------------------------------------
const PlaceholderPoster = ({ discipline = 'general', kind = 'video' }) => {
  const accent = DISCIPLINE_ACCENT[discipline] || DISCIPLINE_ACCENT.general;
  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        background:
          `radial-gradient(circle at 30% 30%, ${accent.hue}55 0%, transparent 55%),` +
          `linear-gradient(160deg, ${accent.hue}22 0%, #0a0705 70%)`,
      }}
      data-testid="netflix-placeholder"
    >
      {/* Diagonal color band on the left edge */}
      <div
        className="absolute top-0 left-0 bottom-0 w-1.5"
        style={{ background: accent.hue }}
      />
      {/* Faint monogram */}
      <img
        src={LOGO_MARK}
        alt=""
        className="absolute right-4 top-4 h-8 opacity-40 pointer-events-none"
      />
      {/* Kind chip bottom-left, above the title overlay */}
      <div
        className="absolute bottom-14 left-3 text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
        style={{ color: BONE, background: 'rgba(0,0,0,0.4)' }}
      >
        {accent.label} · {kind}
      </div>
    </div>
  );
};

// ---- tile primitives -------------------------------------------------------
// Netflix-style tile: base card renders quickly, and on hover (desktop only,
// after ~400ms) it "peeks" — a larger overlay lifts out of the card showing
// description, tags and quick actions. The peek renders in a React portal at
// document.body so parent overflow-x-auto rails never clip it.
const Tile = ({
  href,
  thumb,
  title,
  subtitle,
  description,
  badge,
  portrait = false,
  tags = [],
  noActions = false,
  discipline = 'general',
  kind = 'video',
  onPlay = null,
  // When true, the hover-peek skips the circular ▶ play button but still
  // renders the title/description/tags. Event tiles use this because their
  // click action is "open the clips tray" (handled by EventTileWithTray),
  // not "play a video" — a play button inside the peek is misleading.
  noPlayButton = false,
  viewCount = null,  // number — shows as a small eyeball badge if > 0
  peekContent = null, // optional ReactNode to render inside the hover peek
}) => {
  const [hover, setHover] = useState(false);
  const [rect, setRect] = useState(null);
  const wrapperRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const isHoverDevice = useIsHoverDevice();

  const onEnter = () => {
    if (!isHoverDevice) return; // Skip peek on touch devices
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        setRect(wrapperRef.current.getBoundingClientRect());
      }
      setHover(true);
    }, 400);
  };
  const onLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHover(false);
  };

  const baseClasses = `relative shrink-0 rounded-lg overflow-hidden bg-[#1a1410] border border-white/5 transition-transform duration-200 ${portrait ? `${TILE_W_PORTRAIT} aspect-[2/3]` : `${TILE_W} aspect-video`}`;

  const baseCard = (
    <div className={baseClasses}>
      {thumb ? (
        <img src={thumb} alt={title} loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <PlaceholderPoster discipline={discipline} kind={kind} />
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3 pt-8">
        <div className="text-white text-sm sm:text-base font-semibold truncate">{title}</div>
        {subtitle && <div className="text-xs text-white/70 truncate mt-0.5">{subtitle}</div>}
      </div>
      {badge && (
        <div
          className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm"
          style={{ background: GOLD, color: '#0a0705' }}
        >
          {badge}
        </div>
      )}
      {typeof viewCount === 'number' && viewCount > 0 && (
        <div
          className="absolute top-2 right-2 text-[10px] font-mono px-2 py-0.5 rounded-sm bg-black/70 text-white border border-white/20"
          data-testid="tile-view-count"
          title={`${viewCount} play${viewCount === 1 ? '' : 's'}`}
        >
          ▶ {viewCount >= 1000 ? `${(viewCount / 1000).toFixed(1)}k` : viewCount}
        </div>
      )}
    </div>
  );

  // Tile-wide click target. If onPlay is provided (video tiles), tapping the
  // tile opens the video modal or the subscribe prompt. Otherwise it falls
  // back to a Link to `href` (coaches, upcoming events, static pages).
  const clickable = onPlay
    ? (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onPlay(); }}
          className="block absolute inset-0 z-0 cursor-pointer"
          aria-label={`Play ${title}`}
        />
      )
    : (href ? (
        <Link to={href} className="block absolute inset-0 z-0" aria-label={title} />
      ) : null);

  // "Peek" — rendered in a portal so it escapes parent overflow.
  // Sized ~1.5x the base tile, centered on the tile, clamped inside the viewport.
  const peek = (() => {
    if (!hover || !rect || typeof window === 'undefined') return null;
    const vw = window.innerWidth;
    const margin = 12;
    // ~2x the base tile, clamped inside the viewport.
    const maxByHeight = (window.innerHeight - 200 - margin * 2) / (portrait ? 1.5 : 0.5625);
    const peekWidth = Math.round(Math.min(vw - margin * 2, maxByHeight, rect.width * 2));
    const cx = rect.left + rect.width / 2;
    let left = cx - peekWidth / 2;
    left = Math.max(margin, Math.min(vw - peekWidth - margin, left));
    // Center vertically on the tile, keeping the whole card on screen.
    const estHeight = peekWidth * (portrait ? 1.5 : 0.5625) + 200;
    const top = Math.max(margin, Math.min(window.innerHeight - estHeight - margin, rect.top + rect.height / 2 - estHeight / 2));

    return createPortal(
      <div
        className="hidden md:block fixed z-[100] rounded-lg overflow-hidden bg-[#1a1410] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] border border-[#c8a672]/50 pointer-events-auto"
        style={{ width: peekWidth, top, left }}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        {/* Media */}
        <div className={portrait ? 'aspect-[2/3] bg-black' : 'aspect-video bg-black'}>
          {thumb ? (
            <img src={thumb} alt={title} className="w-full h-full object-cover" />
          ) : (
            <PlaceholderPoster discipline={discipline} kind={kind} />
          )}
        </div>
        {/* Body */}
        <div className="p-4">
          {!noActions && (
            <div className="flex items-center gap-2 mb-2">
              {!noPlayButton && (onPlay || href) && (
                onPlay ? (
                  <button
                    type="button"
                    onClick={onPlay}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                    style={{ background: BONE, color: '#0a0705' }}
                    aria-label="Play"
                    data-testid="peek-play"
                  >
                    <Play className="w-4 h-4 fill-current" />
                  </button>
                ) : (
                  <Link
                    to={href}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                    style={{ background: BONE, color: '#0a0705' }}
                    aria-label="Play"
                    data-testid="peek-play"
                  >
                    <Play className="w-4 h-4 fill-current" />
                  </Link>
                )
              )}
              {badge && (
                <span
                  className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm"
                  style={{ background: GOLD, color: '#0a0705' }}
                >
                  {badge}
                </span>
              )}
            </div>
          )}
          <div className="text-white font-display font-bold text-lg leading-tight mb-1">{title}</div>
          {subtitle && <div className="text-xs uppercase tracking-widest text-white/60 mb-2">{subtitle}</div>}
          {description && (
            <p className="text-sm text-white/80 line-clamp-3 leading-snug">{description}</p>
          )}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {tags.map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-widest text-white/70 border border-white/20 rounded-sm px-1.5 py-0.5">
                  {t}
                </span>
              ))}
            </div>
          )}
          {peekContent && (
            <div className="mt-3 border-t border-white/10 pt-3">
              {peekContent}
            </div>
          )}
        </div>
      </div>,
      document.body,
    );
  })();

  return (
    <div
      ref={wrapperRef}
      className="relative shrink-0"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {baseCard}
      {clickable}
      {peek}
    </div>
  );
};

// Event tile wrapped in a right-edge drawer that reveals the event's attached
// video clips as sibling tiles when the admin/user clicks the handle. Keeps
// the current main-tile click behavior (navigate to /events/{id}/watch or
// openPlayer for legacy) so nothing today breaks — the handle is additive.
// Hidden entirely when the event has zero attached clips so there's never an
// empty drawer. Below the sm breakpoint the drawer stacks under the tile
// instead of sliding sideways (horizontal scroll gets clunky on phones).
// Event tile with inline slide-out clip tray. The handle on the right edge
// is an iOS-style vertical pill (same visual language as the drag indicator
// on an iOS bottom sheet, just rotated vertically). Clicking the handle OR
// the main tile image opens the tray — clips slide out horizontally to the
// right of the main tile as full-size sibling tiles. Hidden entirely when
// the event has zero attached clips so empty tiles keep their original
// /events/{id}/watch href link behavior.
const EventTileWithTray = ({ event, clips = [], openPlayer, children }) => {
  const [open, setOpen] = useState(false);
  const hasClips = Array.isArray(clips) && clips.length > 0;
  const toggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen((o) => !o);
  };

  // Only intercept the Tile's click when there's actually a tray to open.
  // Events with no clips keep their original href/onPlay behavior.
  const mainTile = hasClips && React.isValidElement(children)
    ? React.cloneElement(children, {
        onPlay: () => setOpen((o) => !o),
        href: undefined,
        noPlayButton: true,
      })
    : children;

  return (
    <div
      className={`flex items-stretch shrink-0 ${open ? 'flex-col sm:flex-row gap-3' : 'gap-0'}`}
      data-testid={`event-tray-${event.id}`}
    >
      <div className="relative shrink-0">
        {mainTile}
        {hasClips && (
          <button
            type="button"
            onClick={toggle}
            aria-label={open ? 'Hide clips' : 'Show clips'}
            data-testid={`event-tray-handle-${event.id}`}
            // Narrow vertical tab stuck to the right edge — text runs
            // top-to-bottom so the tab itself stays slim (~22px) and never
            // overlaps the clip tiles that fan out beside it.
            className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-full flex-col items-center justify-center z-30 py-3 w-[22px] gap-1.5 rounded-r-md shadow-[2px_2px_10px_rgba(0,0,0,0.7)] transition-transform duration-200 hover:translate-x-0.5 hover:-translate-y-1/2"
            style={{
              background: '#0f0a07',
              border: '1px solid #c8a672',
              borderLeft: 'none',
              color: '#f3d58a',
            }}
            title={open ? 'Hide clips' : 'Show clips'}
          >
            <span
              className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] leading-none"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {open ? 'Hide' : 'Show'}
            </span>
            <span
              className="text-[10px] leading-none inline-block transition-transform duration-300"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              ▶
            </span>
          </button>
        )}
        {hasClips && (
          <button
            type="button"
            onClick={toggle}
            aria-label={open ? 'Close video tray' : 'Open video tray'}
            data-testid={`event-tray-handle-mobile-${event.id}`}
            className="sm:hidden absolute bottom-2 right-2 z-20 bg-black/80 hover:bg-black/95 border border-white/15 rounded-full px-2 py-1 text-[10px] text-white font-mono uppercase tracking-widest flex items-center gap-1"
          >
            {open ? 'Hide' : `+${clips.length} clip${clips.length === 1 ? '' : 's'}`}
            <span
              className="transition-transform duration-300 inline-block"
              style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}
            >
              ▶
            </span>
          </button>
        )}
      </div>
      {open && (
        <div
          className="flex items-stretch gap-2 overflow-x-auto sm:overflow-visible pl-2 sm:pl-4"
          data-testid={`event-tray-contents-${event.id}`}
        >
          {clips.map((v) => {
            const src = pickSource(v);
            const evName = event.homepage_title || event.title;
            return (
              <button
                type="button"
                key={`clip-${v.id}`}
                onClick={() => openPlayer(
                  { title: clipLabel(v, evName), embed_code: src, id: v.id },
                  { kind: 'event-video' },
                )}
                className={`relative shrink-0 ${TILE_W} aspect-video rounded-lg overflow-hidden bg-[#1a1410] border border-white/10 group text-left transition-transform duration-200 hover:scale-[1.02]`}
                data-testid={`event-tray-clip-${v.id}`}
              >
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt={clipName(v) || evName || ''} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">No thumb</div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3 pt-8">
                  <div className="text-[11px] uppercase tracking-widest text-white/60 truncate">{evName}</div>
                  <div className="text-white text-sm sm:text-base font-semibold truncate mt-0.5">{clipName(v) || 'Clip'}</div>
                </div>
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ background: 'rgba(0,0,0,0.25)' }}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(243,213,138,0.95)', color: '#0a0705' }}>
                    <span className="text-xl pl-0.5">▶</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Coach tile with lazy-loaded "all their videos" peek. The videos call fires
// on first hover (not on initial render) so a rail of 20 coaches doesn't fan
// out 20 API calls up front.
const CoachTile = ({ coach, isFeatured }) => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState(null);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (!hovered || videos !== null) return;
    axios.get(`${API_URL}/api/coaches/${coach.id}/videos`, { timeout: 6000 })
      .then((r) => setVideos(r.data?.videos || []))
      .catch(() => setVideos([]));
  }, [hovered, videos, coach.id]);

  const peekBody = (
    <div onMouseEnter={() => setHovered(true)} data-testid={`coach-peek-${coach.id}`}>
      {videos === null && (
        <div className="text-xs text-white/60">Loading videos…</div>
      )}
      {videos && videos.length === 0 && (
        <div className="text-xs text-white/60">No videos linked to this coach yet.</div>
      )}
      {videos && videos.length > 0 && (
        <>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2">
            {videos.length} video{videos.length === 1 ? '' : 's'}
          </div>
          <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
            {videos.slice(0, 8).map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => {
                  if (v.kind === 'event-video' && v.event_id) {
                    navigate(`/events/${v.event_id}/watch?v=${v.id}`);
                  } else {
                    navigate('/browse');
                  }
                }}
                className="w-full text-left flex items-center gap-2 p-1.5 rounded hover:bg-white/5 text-xs"
                data-testid={`coach-peek-video-${v.id}`}
              >
                {v.thumbnail_url ? (
                  <img src={v.thumbnail_url} alt="" className="w-14 h-9 object-cover rounded flex-shrink-0" />
                ) : (
                  <div className="w-14 h-9 bg-black/60 rounded flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">{v.title}</div>
                  {v.event_title && (
                    <div className="text-white/50 text-[10px] truncate">from {v.event_title}</div>
                  )}
                </div>
                {typeof v.view_count === 'number' && v.view_count > 0 && (
                  <span className="text-[10px] text-white/60 whitespace-nowrap">▶ {v.view_count}</span>
                )}
              </button>
            ))}
            {videos.length > 8 && (
              <div className="text-[10px] text-white/50 italic pt-1">+ {videos.length - 8} more…</div>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div onMouseEnter={() => setHovered(true)}>
      <Tile
        key={coach.id}
        thumb={coach.photo_url}
        title={coach.name}
        subtitle={coach.region || ''}
        description={coach.bio || coach.tagline || ''}
        tags={(coach.disciplines || []).slice(0, 4)}
        badge={isFeatured ? 'Featured' : null}
        portrait
        noActions
        discipline="coach"
        kind="Coach"
        href={`/coaches/${coach.id}`}
        peekContent={peekBody}
      />
    </div>
  );
};

// ---- hero ------------------------------------------------------------------
const Hero = ({ liveStream, featured, heroImages = [], freeVideo, onWatch, onLearnMore }) => {
  const isLive = !!liveStream?.is_live;
  // Priority order for what the hero *shows*:
  // 1. Live stream (if on)
  // 2. Admin-pinned "featured free" video (an actual playable preview)
  // 3. The generic landing copy
  const hasFree = !isLive && !!freeVideo?.embed_code;
  const heroTitle = isLive
    ? (liveStream.title || 'Live Now')
    : hasFree
    ? (freeVideo.title || 'Watch now — free')
    : (featured?.title || 'Every event. Live from the arena.');
  const heroSubtitle = isLive
    ? 'Streaming live right now — tap in to watch the run happen in real time.'
    : hasFree
    ? (freeVideo.description || 'Free preview — press play and see what Elevated looks like.')
    : 'Private membership for the rodeo community. Live event streams, critiques from real coaches, photos and videos of every ride, all under one roof.';

  // Rotating slideshow backdrop (only when nothing is live). Cycles through
  // whatever real photos we have from past events / library / upcoming so the
  // hero feels alive instead of a static image.
  const [slideIdx, setSlideIdx] = useState(0);
  const slides = useMemo(() => {
    if (isLive) {
      return [liveStream?.thumbnail_url].filter(Boolean);
    }
    // When a free preview is pinned, lock the hero to its thumbnail — no
    // rotation. The viewer sees the actual clip they're about to play.
    if (hasFree) {
      return freeVideo?.thumbnail_url ? [freeVideo.thumbnail_url] : [];
    }
    // De-dupe by URL to avoid the same image showing twice in the loop
    return Array.from(new Set(heroImages.filter(Boolean)));
  }, [isLive, liveStream, heroImages, hasFree, freeVideo]);
  useEffect(() => {
    if (isLive || slides.length < 2) return;
    const t = setInterval(() => {
      setSlideIdx((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(t);
  }, [isLive, slides.length]);

  return (
    <section className="relative min-h-[60vh] sm:min-h-[85vh] flex items-end pb-10 sm:pb-24 px-4 sm:px-8 overflow-hidden" data-testid="netflix-hero">
      {/* Background */}
      <div className="absolute inset-0">
        {slides.length > 0 ? (
          slides.map((src, i) => (
            <img
              key={`slide-${i}-${src}`}
              src={src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms]"
              style={{ opacity: i === slideIdx ? 0.65 : 0, objectPosition: 'center 25%' }}
              data-testid={i === slideIdx ? 'netflix-hero-slide-active' : undefined}
            />
          ))
        ) : (
          <div
            className="w-full h-full"
            style={{
              background:
                'radial-gradient(ellipse at 30% 40%, rgba(200,166,114,0.15), transparent 60%), linear-gradient(180deg, #0a0705 0%, #1a1108 60%, #0a0705 100%)',
            }}
          />
        )}
        {/* Cinematic vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,7,5,0.4) 0%, rgba(10,7,5,0.1) 40%, rgba(10,7,5,0.9) 90%, #0a0705 100%)',
          }}
        />
      </div>

      <div className="relative z-10 max-w-3xl">
        {isLive && (
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border border-red-500/60 bg-red-950/40">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-300">Live Now</span>
          </div>
        )}
        {!isLive && hasFree && (
          <div
            className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest"
            style={{ color: GOLD, borderColor: `${GOLD}80`, borderWidth: 1, borderStyle: 'solid', background: `${GOLD}11` }}
            data-testid="netflix-hero-free-badge"
          >
            <Play className="w-3 h-3 fill-current" />
            <span>Free Preview</span>
          </div>
        )}
        <h1
          className="font-display font-bold uppercase text-3xl sm:text-6xl lg:text-7xl leading-[0.95] sm:leading-[0.9] tracking-tight mb-4 sm:mb-6"
          style={{ color: BONE }}
        >
          {heroTitle}
        </h1>
        <p className="text-sm sm:text-lg text-white/80 max-w-2xl mb-6 sm:mb-8" style={{ fontFamily: "'Libre Baskerville', serif", fontStyle: 'italic' }}>
          {heroSubtitle}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onWatch}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-105"
            style={{ background: BONE, color: '#0a0705' }}
            data-testid="netflix-hero-primary"
          >
            <Play className="w-5 h-5 fill-current" />
            {isLive ? 'Watch Live' : hasFree ? 'Watch Free' : 'Start Watching'}
          </button>
          <button
            type="button"
            onClick={onLearnMore}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] rounded-md font-semibold transition-all hover:scale-105 border border-white/30 text-white bg-white/10 backdrop-blur"
            data-testid="netflix-hero-secondary"
          >
            <Info className="w-5 h-5" />
            More Info
          </button>
        </div>
      </div>
    </section>
  );
};

// ---- top nav ---------------------------------------------------------------
const TopNav = ({ query, onQuery, onSubscribe }) => {
  const [open, setOpen] = useState(!!query);
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const { user, isAuthenticated, isAdmin, isCoach, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Close the account dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const initials = (user?.name || user?.email || 'E')
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || 'E';
  const subStatus = user?.subscription_status || 'none';
  const subLabel = isAdmin
    ? 'Admin'
    : isCoach
    ? 'Coach'
    : subStatus === 'active'
    ? 'Subscriber'
    : subStatus === 'trial'
    ? 'Trial'
    : subStatus === 'cancelled'
    ? 'Cancelled'
    : 'No subscription';
  const subDotColor = (isAdmin || isCoach || subStatus === 'active' || subStatus === 'trial')
    ? '#4ade80'
    : '#f87171';

  return (
    <nav
      className="fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 sm:px-8 py-4 transition-all"
      style={{ background: 'linear-gradient(180deg, rgba(10,7,5,0.9) 0%, rgba(10,7,5,0) 100%)' }}
    >
      <Link to="/" className="flex items-center gap-3" data-testid="netflix-logo">
        <img
          src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png"
          alt="Elevated"
          className="h-10 w-auto"
        />
        <div className="hidden sm:flex flex-col leading-none">
          <span className="font-display font-bold text-xs tracking-widest" style={{ color: BONE }}>ELEVATED</span>
          <span className="font-display text-[10px] tracking-widest" style={{ color: GOLD }}>ROPING · MEDIA</span>
        </div>
      </Link>
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Search — collapses to an icon when idle, expands on click */}
        <div className="flex items-center">
          {open ? (
            <div
              className="flex items-center gap-1 border border-white/40 bg-black/70 rounded-md px-2 sm:px-3 py-1.5 transition-all"
              style={{ width: 220 }}
            >
              <Search className="w-4 h-4 text-white/70" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Titles, coaches, events"
                className="bg-transparent outline-none text-sm w-full placeholder-white/40"
                style={{ color: BONE }}
                data-testid="netflix-search-input"
              />
              <button
                type="button"
                aria-label="Close search"
                onClick={() => { onQuery(''); setOpen(false); }}
                className="text-white/60 hover:text-white"
                data-testid="netflix-search-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              aria-label="Search"
              onClick={() => setOpen(true)}
              className="p-2 rounded-md hover:bg-white/10 transition-colors"
              data-testid="netflix-search-open"
            >
              <Search className="w-5 h-5" style={{ color: BONE }} />
            </button>
          )}
        </div>
        <Link
          to="/browse"
          className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-sm font-semibold rounded-md hover:bg-white/10 transition-colors"
          style={{ color: BONE }}
          data-testid="netflix-library-link"
        >
          <Library className="w-5 h-5" />
          <span className="hidden sm:inline">Library</span>
        </Link>
        {!isAuthenticated && (
          <Link
            to="/login"
            className="hidden sm:inline-block px-4 py-2 text-sm font-semibold hover:opacity-80 transition-opacity"
            style={{ color: BONE }}
            data-testid="netflix-signin"
          >
            Sign in
          </Link>
        )}
        {!isAuthenticated && (
          <button
            type="button"
            onClick={() => onSubscribe && onSubscribe()}
            className="px-3 sm:px-4 py-2 text-sm font-semibold rounded-md transition-all hover:scale-105"
            style={{ background: GOLD, color: '#0a0705' }}
            data-testid="netflix-subscribe"
          >
            Subscribe
          </button>
        )}
        {isAuthenticated && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-white/20 bg-black/50 hover:bg-white/10 transition-colors"
              data-testid="netflix-account-button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: GOLD, color: '#0a0705' }}
              >
                {initials}
              </span>
              <span className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-xs font-semibold" style={{ color: BONE }}>
                  {user?.name || user?.email?.split('@')[0] || 'Account'}
                </span>
                <span className="text-[10px] flex items-center gap-1" style={{ color: '#cbd5e1' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: subDotColor }} />
                  {subLabel}
                </span>
              </span>
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-56 rounded-lg overflow-hidden border border-white/10 shadow-xl z-40"
                style={{ background: '#141010' }}
                role="menu"
                data-testid="netflix-account-menu"
              >
                <div className="px-4 py-3 border-b border-white/10">
                  <div className="text-xs font-semibold truncate" style={{ color: BONE }}>
                    {user?.email || ''}
                  </div>
                  <div className="text-[11px] mt-0.5 flex items-center gap-1.5" style={{ color: '#cbd5e1' }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: subDotColor }} />
                    {subLabel}
                  </div>
                </div>
                <Link
                  to="/browse?tab=saved"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-white hover:bg-white/5"
                  role="menuitem"
                  data-testid="netflix-account-mylist"
                >
                  My List
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-white hover:bg-white/5"
                  role="menuitem"
                  data-testid="netflix-account-profile"
                >
                  Profile
                </Link>
                <Link
                  to="/subscription"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-white hover:bg-white/5"
                  role="menuitem"
                  data-testid="netflix-account-subscription"
                >
                  Subscription
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-white hover:bg-white/5"
                    role="menuitem"
                    data-testid="netflix-account-admin"
                  >
                    Admin
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                    navigate('/');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/5 border-t border-white/10"
                  role="menuitem"
                  data-testid="netflix-account-logout"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

// ---- search results ------------------------------------------------------
// Renders when the user has typed something. Bundles results into groups so
// people can scan by type. Uses the same Tile primitive so it feels native.
const SearchResults = ({ query, results, total, onPlay, onCoachInfo, onEventInfo }) => (
  <div className="pt-28 pb-24 px-4 sm:px-8" data-testid="netflix-search-results">
    <div className="max-w-6xl mx-auto">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Search</div>
      <h1
        className="font-display font-bold uppercase text-3xl sm:text-4xl mb-2"
        style={{ color: BONE }}
      >
        Results for &ldquo;{query}&rdquo;
      </h1>
      <div className="text-sm text-white/60 mb-10">
        {total} {total === 1 ? 'match' : 'matches'} across coaches, library, and events
      </div>

      {total === 0 && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-8 text-center">
          <div className="text-white/70 text-base mb-2">Nothing matched that search.</div>
          <div className="text-xs text-white/40">
            Try a coach name, a discipline (heading, breakaway), or an event title.
          </div>
        </div>
      )}

      {results.coaches.length > 0 && (
        <ResultsGroup title="Coaches" testid="search-group-coaches">
          {results.coaches.map((c) => (
            <Tile
              key={c.id}
              thumb={c.photo_url}
              title={c.name}
              subtitle={c.region || ''}
              description={c.bio || c.tagline || ''}
              tags={(c.disciplines || []).slice(0, 4)}
              portrait
              noActions
              discipline="coach"
              kind="Coach"
              onPlay={() => onCoachInfo(c)}
            />
          ))}
        </ResultsGroup>
      )}

      {results.library.length > 0 && (
        <ResultsGroup title="Library" testid="search-group-library">
          {results.library.map((v) => (
            <Tile
              key={v.id}
              thumb={fallbackThumb(v)}
              title={v.title}
              subtitle={[v.category, v.difficulty].filter(Boolean).join(' · ')}
              description={v.description || ''}
              tags={[v.category, v.difficulty].filter(Boolean)}
              badge={v.difficulty === 'advanced' ? 'Pro' : null}
              discipline={(v.category || 'general').toLowerCase()}
              kind="Video"
              onPlay={() => onPlay(v)}
            />
          ))}
        </ResultsGroup>
      )}

      {results.upcoming.length > 0 && (
        <ResultsGroup title="Upcoming Events" testid="search-group-upcoming">
          {results.upcoming.map((e) => (
            <Tile
              key={e.id}
              thumb={e.image_url}
              title={e.title}
              subtitle={e.event_date || 'TBA'}
              description={e.description || ''}
              tags={[e.event_type, e.location].filter(Boolean)}
              discipline="event"
              kind="Event"
              onPlay={() => onEventInfo(e)}
            />
          ))}
        </ResultsGroup>
      )}

      {results.past.length > 0 && (
        <ResultsGroup title="Past Events" testid="search-group-past">
          {results.past.map((e) => (
            <Tile
              key={e.id}
              thumb={e.thumbnail_url}
              title={e.title}
              subtitle={e.event_date || ''}
              description={e.description || ''}
              discipline="event"
              kind="Replay"
              onPlay={() => onPlay(e)}
            />
          ))}
        </ResultsGroup>
      )}
    </div>
  </div>
);

const ResultsGroup = ({ title, testid, children }) => (
  <section className="mb-10" data-testid={testid}>
    <h2 className="font-display uppercase text-lg mb-3 tracking-wide" style={{ color: BONE }}>
      {title}
    </h2>
    <div className="flex flex-wrap gap-4">{children}</div>
  </section>
);

// ---- page ------------------------------------------------------------------
const NetflixLandingPage = () => {
  const [live, setLive] = useState(null);
  const [library, setLibrary] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [eventVideos, setEventVideos] = useState([]);
  const [eventPhotos, setEventPhotos] = useState([]);
  const [teasers, setTeasers] = useState([]);
  const [liveTeasers, setLiveTeasers] = useState([]);
  const [studioProjects, setStudioProjects] = useState([]);
  const [featuredFree, setFeaturedFree] = useState(null); // {kind, id, title, description, thumbnail_url, embed_code}
  const [homeRails, setHomeRails] = useState(null); // config from /api/home-config
  const [taxonomies, setTaxonomies] = useState(null); // {video_category: [...], ...}
  const [query, setQuery] = useState('');
  // Playback state — the tile Play button opens either the video modal (if
  // the viewer is authenticated AND subscribed) or a sign-in / subscribe prompt.
  const [playing, setPlaying] = useState(null); // { title, embedCode } | null
  const [loginPrompt, setLoginPrompt] = useState(null); // { title, mode } | null
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [info, setInfo] = useState(null); // record for InfoModal | null
  const { user } = useAuth();
  const saved = useSaved(!!user);
  // `isAuthed` drives data-fetching endpoint selection (/library vs
  // /preview/library). We derive it synchronously from the token so the
  // authed library (with real `embed_code`s) is requested on first paint
  // instead of waiting for the AuthContext /auth/me round-trip — otherwise
  // tiles briefly carry preview records with no playable source and clicking
  // one shows "no playable source yet".
  const isAuthed = typeof window !== 'undefined' && !!localStorage.getItem('token');
  // Admins/coaches always have access. Everyone else needs an active or
  // trial subscription — matches the EventWatchPage / LibraryPage gating.
  const canWatch = !!user && (
    user.role === 'admin'
    || user.role === 'coach'
    || user.subscription_status === 'active'
    || user.subscription_status === 'trial'
  );

  useEffect(() => {
    // Each fetch updates its own state the moment it resolves. The hero and
    // any ready rail paints before slow endpoints finish — users don't stare
    // at a blank page because /api/preview/library took 2s to warm up.
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    const TIMEOUT = 6000; // tighter so a stuck pod doesn't hold the UI

    const safe = (promise, onOk) => {
      promise.then(onOk).catch(() => { /* best-effort — missing data just omits that rail */ });
    };

    safe(axios.get(`${API_URL}/api/live-stream`, { timeout: TIMEOUT }),
      (r) => setLive(r.data));
    safe(axios.get(`${API_URL}/api/featured-free-video`, { timeout: TIMEOUT }),
      (r) => setFeaturedFree(r.data?.video || null));
    safe(axios.get(`${API_URL}/api/home-config`, { timeout: TIMEOUT }),
      (r) => setHomeRails(r.data?.rails || null));    safe(axios.get(`${API_URL}/api/taxonomies`, { timeout: TIMEOUT }),
      (r) => setTaxonomies(r.data?.taxonomies || null));
    safe(axios.get(`${API_URL}/api/coaches`, { timeout: TIMEOUT }),
      (r) => setCoaches((r.data?.coaches || []).filter((c) => !isTestRecord(c))));
    safe(axios.get(`${API_URL}/api/events/upcoming`, { timeout: TIMEOUT }),
      (r) => setUpcoming((r.data?.events || []).filter((e) => !isTestRecord(e))));
    safe(axios.get(`${API_URL}/api/past-events`, { timeout: TIMEOUT }),
      (r) => setPast((r.data?.events || []).filter((e) => !isTestRecord(e))));

    // Event-attached videos (clips inside each event — "Round 1", "Slack"
    // etc). The public endpoint groups them by event; we flatten so a tile
    // can be rendered for any single clip the admin pinned into a rail.
    // Request a large per-event slice so pinned clips deep in a big event
    // archive still resolve (default was 12 which hid later clips).
    safe(axios.get(`${API_URL}/api/event-videos?per_event=2000`, { timeout: TIMEOUT }),
      (r) => {
        const flat = [];
        for (const group of (r.data?.events || [])) {
          for (const v of (group.videos || [])) flat.push(v);
        }
        setEventVideos(flat);
      });

    // Event photos — flatten the group-by-event payload so a tile for any
    // specific photo pinned into a rail can resolve by id.
    safe(axios.get(`${API_URL}/api/event-photos?per_event=500`, { timeout: TIMEOUT }),
      (r) => {
        const flat = [];
        for (const group of (r.data?.events || [])) {
          for (const p of (group.photos || [])) flat.push(p);
        }
        setEventPhotos(flat);
      });

    // Teasers (vertical 9:16) and live-event teasers — both stored in the
    // settings doc as [{url, title}]. We key by url (that's what the picker
    // used as the item id). Only authed subs will actually be able to play
    // the final rendered studio projects, but anyone can preview teasers.
    safe(axios.get(`${API_URL}/api/teaser-videos`, { timeout: TIMEOUT }),
      (r) => setTeasers((r.data?.videos || []).filter((t) => (t?.url || '').trim())));
    safe(axios.get(`${API_URL}/api/live-event-teasers`, { timeout: TIMEOUT }),
      (r) => setLiveTeasers((r.data?.videos || []).filter((t) => (t?.url || '').trim())));
    // Video Studio projects — public endpoint returns only published
    // projects with a renderable final_video_url. The admin picker uses the
    // admin-only endpoint which includes drafts/student-specific projects.
    safe(axios.get(`${API_URL}/api/public/video-projects`, { timeout: TIMEOUT }),
      (r) => setStudioProjects(r.data?.projects || []));

    // Prefer the authed library (carries embed_code), fall back to preview.
    if (isAuthed) {
      safe(
        axios.get(`${API_URL}/api/library`, { timeout: TIMEOUT, headers: authHeaders }),
        (r) => setLibrary((r.data?.videos || []).filter((v) => !isTestRecord(v))),
      );
    } else {
      safe(
        axios.get(`${API_URL}/api/preview/library`, { timeout: TIMEOUT }),
        (r) => setLibrary((r.data?.videos || []).filter((v) => !isTestRecord(v))),
      );
    }
  }, [isAuthed]);

  // Hydrate events pinned to custom rails that didn't come back from the
  // /upcoming or /past-events feeds (admin may have pinned a past-dated
  // event that still lives in `events`, or an unpublished one). Without
  // this the whole custom rail silently renders empty and disappears.
  useEffect(() => {
    if (!homeRails) return;
    const needed = new Set();
    for (const rail of homeRails) {
      if (!rail?.custom || !Array.isArray(rail.items)) continue;
      for (const it of rail.items) {
        if (it.kind === 'upcoming' || it.kind === 'past') {
          const found = upcoming.some((e) => e.id === it.id) || past.some((e) => e.id === it.id);
          if (!found) needed.add(it.id);
        }
      }
    }
    if (needed.size === 0) return;
    const ids = Array.from(needed).join(',');
    axios.get(`${API_URL}/api/events/by-ids?ids=${encodeURIComponent(ids)}`, { timeout: 6000 })
      .then((r) => {
        const extra = (r.data?.events || []).filter((e) => !isTestRecord(e));
        if (extra.length === 0) return;
        // Classify each by date → upcoming vs past (so our existing render
        // paths work unchanged). Date absence/past → past bucket.
        const today = new Date().toISOString().slice(0, 10);
        setUpcoming((prev) => {
          const existing = new Set(prev.map((e) => e.id));
          const additions = extra.filter((e) => !existing.has(e.id) && e.event_date && e.event_date >= today);
          return additions.length ? [...prev, ...additions] : prev;
        });
        setPast((prev) => {
          const existing = new Set(prev.map((e) => e.id));
          const additions = extra.filter((e) => !existing.has(e.id) && (!e.event_date || e.event_date < today));
          return additions.length ? [...prev, ...additions] : prev;
        });
      })
      .catch(() => { /* best-effort */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeRails, upcoming.length, past.length]);


  // Play handler shared by every video tile. Not-yet-authenticated viewers
  // get the sign-in / subscribe prompt; logged-in users without an active
  // or trial subscription get a subscribe-only prompt. Admins/coaches
  // always have access. If the record has no embed_code we still show
  // the modal (it renders a friendly "no source" message) rather than
  // dumping them on the old site.
  const openPlayer = useCallback((record, opts = {}) => {
    const title = record?.title || 'Elevated';
    if (!isAuthed) {
      setLoginPrompt({ title, mode: 'login' });
      return;
    }
    // Auth token present but AuthContext hasn't resolved yet — show the
    // generic login/subscribe prompt rather than mis-labelling the viewer
    // as "subscribers only" during the brief hydration window.
    if (!user) {
      setLoginPrompt({ title, mode: 'login' });
      return;
    }
    if (!canWatch) {
      setLoginPrompt({ title, mode: 'subscribe' });
      return;
    }
    // Library rows carry `video_url`; past events carry `embed_code`; live
    // stream carries `embed_code` too. Either works — resolvePlayable in the
    // modal figures out how to render it.
    const source = pickSource(record);
    const saveKind = opts.saveKind || opts.kind || null;
    setPlaying({
      title,
      embedCode: source,
      // Only count views for the two shapes we actually track server-side.
      videoId: opts.kind ? record?.id : null,
      videoKind: opts.kind || null,
      saveKind: record?.id && ['library', 'event-video', 'past', 'studio-project'].includes(saveKind) ? saveKind : null,
      saveId: record?.id || null,
    });
  }, [isAuthed, user, canWatch]);

  // Non-video tiles (coaches, upcoming events) open a details sheet instead
  // of navigating away from /netflix.
  const openCoachInfo = useCallback((c) => {
    setInfo({
      title: c.name,
      image: c.photo_url,
      subtitle: c.region || '',
      description: c.bio || c.tagline || 'Elevated Roping Media coach.',
      meta: (c.disciplines || []).slice(0, 6),
      portrait: false,
      discipline: 'coach',
      kind: 'Coach',
    });
  }, []);

  const openEventInfo = useCallback((e) => {
    setInfo({
      title: e.title,
      image: e.image_url || e.thumbnail_url,
      subtitle: [e.event_date, e.location].filter(Boolean).join(' · ') || 'Upcoming event',
      description: e.description || 'More details coming soon.',
      meta: [e.event_type].filter(Boolean),
      discipline: 'event',
      kind: 'Event',
    });
  }, []);

  // Bucket library videos by discipline for per-discipline rails
  const byDiscipline = useMemo(() => {
    const map = {};
    for (const v of library) {
      const c = (v.category || 'general').toLowerCase();
      if (!map[c]) map[c] = [];
      map[c].push(v);
    }
    return map;
  }, [library]);

  const newInLibrary = useMemo(() => library.slice(0, 20), [library]);

  // Just Added — pooled newest across library + upcoming + past.
  // We rank by `created_at` (falls back to date/id) and take the top 5 so
  // the top of the home page always feels fresh regardless of source.
  const justAdded = useMemo(() => {
    const stamp = (r) =>
      r.created_at || r.uploaded_at || r.event_date || r.date || r.id || '';
    const pool = [
      ...library.map((v) => ({ ...v, __kind: 'library' })),
      ...upcoming.map((e) => ({ ...e, __kind: 'upcoming' })),
      ...past.map((e) => ({ ...e, __kind: 'past' })),
    ];
    pool.sort((a, b) => String(stamp(b)).localeCompare(String(stamp(a))));
    return pool.slice(0, 5);
  }, [library, upcoming, past]);

  // The very first library or past event serves as the hero background
  const featured = past[0] || upcoming[0] || newInLibrary[0] || null;

  // Rotating hero slideshow — pool of real photos from past events + upcoming
  // events + library thumbnails. The hero shuffles through these every 6s
  // when nothing is live, so the top of the page always feels active.
  const heroImages = useMemo(() => {
    const pool = [
      ...past.map((e) => e.thumbnail_url),
      ...upcoming.map((e) => e.image_url),
      ...library.map((v) => v.thumbnail_url),
    ].filter(Boolean);
    return pool.slice(0, 10);
  }, [past, upcoming, library]);

  // ---- search ----
  const trimmed = query.trim().toLowerCase();
  const isSearching = trimmed.length > 0;
  const searchResults = useMemo(() => {
    if (!isSearching) return null;
    const match = (s) => (s || '').toLowerCase().includes(trimmed);
    return {
      coaches: coaches.filter((c) => match(c.name) || match(c.region) || match(c.bio) || (c.disciplines || []).some(match)),
      library: library.filter((v) => match(v.title) || match(v.description) || match(v.category) || match(v.difficulty)),
      upcoming: upcoming.filter((e) => match(e.title) || match(e.description) || match(e.location) || match(e.event_type)),
      past: past.filter((e) => match(e.title) || match(e.description)),
    };
  }, [isSearching, trimmed, coaches, library, upcoming, past]);

  const totalResults = searchResults
    ? searchResults.coaches.length + searchResults.library.length + searchResults.upcoming.length + searchResults.past.length
    : 0;

  return (
    <div className="min-h-screen" style={{ background: BG, color: BONE }}>
      <TopNav query={query} onQuery={setQuery} onSubscribe={() => setSubscribeOpen(true)} />

      {isSearching ? (
        <SearchResults
          query={query}
          results={searchResults}
          total={totalResults}
          onPlay={openPlayer}
          onCoachInfo={openCoachInfo}
          onEventInfo={openEventInfo}
        />
      ) : (
        <>
          <Hero
            liveStream={live}
            featured={featuredFree || featured}
            heroImages={heroImages}
            freeVideo={featuredFree}
            onWatch={() => {
              // 1) Live is on — same gating as before (free_for_all skips sub)
              if (live?.is_live) {
                if (live.free_for_all) {
                  setPlaying({ title: live.title || 'Live Event', embedCode: live.embed_code || '' });
                } else {
                  openPlayer({ title: live.title, embed_code: live.embed_code });
                }
                return;
              }
              // 2) Admin pinned a free featured video — anyone can watch it,
              // no subscription required. This is the whole point of the free
              // featured slot: give new visitors an actual preview.
              if (featuredFree?.embed_code) {
                setPlaying({ title: featuredFree.title || 'Featured', embedCode: featuredFree.embed_code });
                return;
              }
              // 3) Nothing featured — scroll to the first content rail
              const rail = document.querySelector('[data-testid^="netflix-rail-"]');
              if (rail) rail.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            onLearnMore={() => {
              // Show a details card describing what Elevated is — keeps the
              // viewer on /netflix instead of jumping to /coaches or /subscription.
              setInfo({
                title: 'Elevated Roping Media',
                image: featured?.thumbnail_url || featured?.image_url,
                subtitle: 'Private membership · Live + On-demand',
                description:
                  'Every event streamed live from the arena, video critiques from real coaches, and photo + video coverage of every ride — all in one place. Subscribe to unlock the library, or catch our free sponsored streams as they happen.',
                meta: ['Live Events', 'Video Critique', 'Library', 'Photos + Video'],
                discipline: 'event',
                kind: 'Membership',
              });
            }}
          />

          {/* Rails — order/enabled/featured driven by /api/home-config.
              Live rail is always first when a broadcast is on-air; it's not
              part of the admin toggle set (owners always want the live one). */}
          <div className="pb-24 space-y-2">
            {live?.is_live && (
              <Rail
                title="🔴 Live Now"
                items={[live]}
                testid="netflix-rail-live"
                renderTile={(l) => (
                  <Tile
                    key="live-tile"
                    thumb={null}
                    title={l.title || 'Live Event'}
                    subtitle={l.free_for_all ? 'Free — sponsor coverage' : 'Subscriber exclusive'}
                    description={l.free_for_all
                      ? 'Sponsor-covered stream — no login required. Tap in to watch the arena live.'
                      : 'Subscriber-exclusive live event. Sign in to keep watching.'}
                    badge="Live"
                    discipline="event"
                    kind="Live"
                    onPlay={l.free_for_all
                      ? () => setPlaying({ title: l.title || 'Live Event', embedCode: l.embed_code || '' })
                      : () => openPlayer({ title: l.title, embed_code: l.embed_code })}
                  />
                )}
              />
            )}

            {(homeRails || [
              { key: 'just-added',  label: 'Just Added',              enabled: true },
              { key: 'new-library', label: 'New in the Library',      enabled: true },
              { key: 'coaches',     label: 'Now Coaching',            enabled: true },
              { key: 'disciplines', label: 'By Discipline',           enabled: true },
              { key: 'upcoming',    label: 'Upcoming Events',         enabled: true },
              { key: 'past',        label: 'Watch Again — Past Events', enabled: true },
            ]).map((rail) => {
              if (!rail.enabled || rail.archived) return null;
              const pin = (arr, id) => {
                if (!id) return arr;
                const idx = arr.findIndex((x) => x.id === id);
                if (idx <= 0) return arr;
                const copy = arr.slice();
                copy.unshift(copy.splice(idx, 1)[0]);
                return copy;
              };

              if (rail.key === 'just-added') {
                if (justAdded.length === 0) return null;
                return (
                  <Rail
                    key="rail-just-added"
                    title={rail.label}
                    items={(() => {
                      // If admin pinned an item that isn't already in the
                      // top-5 pool, inject it at the front so the pin works
                      // for any recent-ish library/event item.
                      const base = justAdded.slice();
                      if (rail.featured_id && !base.some((x) => x.id === rail.featured_id)) {
                        const lib = library.find((v) => v.id === rail.featured_id);
                        const up = upcoming.find((e) => e.id === rail.featured_id);
                        const pa = past.find((e) => e.id === rail.featured_id);
                        const extra = lib
                          ? { ...lib, __kind: 'library' }
                          : up
                            ? { ...up, __kind: 'upcoming' }
                            : pa
                              ? { ...pa, __kind: 'past' }
                              : null;
                        if (extra) return [extra, ...base];
                      }
                      return pin(base, rail.featured_id);
                    })()}
                    testid="netflix-rail-just-added"
                    renderTile={(x) => {
                      const isEvent = x.__kind === 'upcoming' || x.__kind === 'past';
                      const isFeatured = rail.featured_id && x.id === rail.featured_id;
                      return (
                        <Tile
                          key={`ja-${x.__kind}-${x.id}`}
                          thumb={isEvent ? (x.thumbnail_url || x.image_url) : fallbackThumb(x)}
                          title={x.title}
                          subtitle={isEvent
                            ? (x.event_date || (x.__kind === 'upcoming' ? 'Upcoming' : 'Past event'))
                            : [x.category, x.difficulty].filter(Boolean).join(' · ')}
                          description={x.description || ''}
                          tags={isEvent
                            ? [x.__kind === 'upcoming' ? 'Upcoming' : 'Past', x.event_type, x.location].filter(Boolean)
                            : [x.category, x.difficulty].filter(Boolean)}
                          badge={isFeatured ? 'Featured' : 'New'}
                          discipline={isEvent ? 'event' : (x.category || 'general').toLowerCase()}
                          kind={isEvent ? (x.__kind === 'upcoming' ? 'Event' : 'Replay') : 'Video'}
                          onPlay={() => {
                            if (x.__kind === 'upcoming') return openEventInfo(x);
                            return openPlayer(x);
                          }}
                        />
                      );
                    }}
                  />
                );
              }

              if (rail.key === 'new-library') {
                if (newInLibrary.length === 0 && !rail.featured_id) return null;
                const injected = (() => {
                  const base = newInLibrary.slice();
                  if (rail.featured_id && !base.some((v) => v.id === rail.featured_id)) {
                    const extra = library.find((v) => v.id === rail.featured_id);
                    if (extra) return [extra, ...base];
                  }
                  return pin(base, rail.featured_id);
                })();
                return (
                  <Rail
                    key="rail-new-library"
                    title={rail.label}
                    items={injected}
                    testid="netflix-rail-new-library"
                    renderTile={(v) => (
                      <Tile
                        key={v.id}
                        thumb={fallbackThumb(v)}
                        title={v.title}
                        subtitle={[v.category, v.difficulty].filter(Boolean).join(' · ')}
                        description={v.description || ''}
                        tags={[v.category, v.difficulty].filter(Boolean)}
                        badge={rail.featured_id && v.id === rail.featured_id
                          ? 'Featured'
                          : (v.difficulty === 'advanced' ? 'Pro' : null)}
                        discipline={(v.category || 'general').toLowerCase()}
                        kind="Video"
                        viewCount={v.view_count || 0}
                        onPlay={() => openPlayer(v, { kind: 'library' })}
                      />
                    )}
                  />
                );
              }

              if (rail.key === 'coaches') {
                if (coaches.length === 0) return null;
                return (
                  <Rail
                    key="rail-coaches"
                    title={rail.label}
                    items={pin(coaches, rail.featured_id)}
                    testid="netflix-rail-coaches"
                    renderTile={(c) => (
                      <CoachTile key={c.id} coach={c} isFeatured={!!rail.featured_id && c.id === rail.featured_id} />
                    )}
                  />
                );
              }

              if (rail.key === 'disciplines') {
                const hidden = new Set(rail.disciplines_hidden || []);
                const hiddenVideos = new Set(rail.disciplines_hidden_videos || []);
                const customLabels = rail.discipline_labels || {};
                // Auto-title an unknown category key into a friendly label so
                // brand-new categories don't need any admin intervention.
                const titleize = (k) =>
                  String(k || 'general')
                    .replace(/[-_]+/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase());
                // Build the final list of disciplines to render. Start with
                // the seven canonical ones (in fixed order) so heading stays
                // first, then append any other keys we discover (from taxonomy
                // or from video categories) in alphabetical order.
                const canonicalOrder = DISCIPLINE_ORDER.map((d) => d.key);
                const canonicalSet = new Set(canonicalOrder);
                const taxCats = (taxonomies?.video_category || []).map((x) => String(x).toLowerCase());
                const libCats = Object.keys(byDiscipline || {});
                const extraKeys = [...new Set([...taxCats, ...libCats])]
                  .filter((k) => !canonicalSet.has(k))
                  .sort();
                const finalDisciplines = [
                  ...DISCIPLINE_ORDER,
                  ...extraKeys.map((k) => ({ key: k, label: titleize(k) })),
                ];
                return (
                  <React.Fragment key="rail-disciplines">
                    {finalDisciplines.map(({ key, label }) => {
                      if (hidden.has(key)) return null;
                      const items = (byDiscipline[key] || []).filter((v) => !hiddenVideos.has(v.id));
                      if (items.length === 0) return null;
                      const displayLabel = customLabels[key] || label;
                      return (
                        <Rail
                          key={`row-${key}`}
                          title={displayLabel}
                          items={items}
                          testid={`netflix-rail-cat-${key}`}
                          renderTile={(v) => (
                            <Tile
                              key={v.id}
                              thumb={fallbackThumb(v)}
                              title={v.title}
                              subtitle={v.difficulty || ''}
                              description={v.description || ''}
                              tags={[v.difficulty].filter(Boolean)}
                              discipline={key}
                              kind="Video"
                              viewCount={v.view_count || 0}
                              onPlay={() => openPlayer(v, { kind: 'library' })}
                            />
                          )}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              }

              if (rail.key === 'upcoming') {
                if (upcoming.length === 0) return null;
                return (
                  <Rail
                    key="rail-upcoming"
                    title={rail.label}
                    items={pin(upcoming, rail.featured_id)}
                    testid="netflix-rail-upcoming"
                    renderTile={(e) => {
                      const clips = eventVideos.filter((v) => v.event_id === e.id);
                      return (
                        <EventTileWithTray key={e.id} event={e} clips={clips} openPlayer={openPlayer}>
                          <Tile
                            thumb={e.image_url}
                            title={e.title}
                            subtitle={e.event_date || 'TBA'}
                            description={e.description || ''}
                            tags={[e.event_type, e.location].filter(Boolean)}
                            badge={rail.featured_id && e.id === rail.featured_id
                              ? 'Featured'
                              : (e.event_type === 'competition' ? 'Comp' : null)}
                            discipline="event"
                            kind="Event"
                            onPlay={() => openEventInfo(e)}
                          />
                        </EventTileWithTray>
                      );
                    }}
                  />
                );
              }

              if (rail.key === 'past') {
                if (past.length === 0) return null;
                return (
                  <Rail
                    key="rail-past"
                    title={rail.label}
                    items={pin(past, rail.featured_id)}
                    testid="netflix-rail-past"
                    renderTile={(e) => {
                      const clips = eventVideos.filter((v) => v.event_id === e.id);
                      return (
                        <EventTileWithTray key={e.id} event={e} clips={clips} openPlayer={openPlayer}>
                          <Tile
                            thumb={e.thumbnail_url}
                            title={e.title}
                            subtitle={e.event_date || ''}
                            description={e.description || ''}
                            badge={rail.featured_id && e.id === rail.featured_id ? 'Featured' : null}
                            discipline="event"
                            kind="Replay"
                            onPlay={() => openPlayer(e, { saveKind: 'past' })}
                          />
                        </EventTileWithTray>
                      );
                    }}
                  />
                );
              }

              // Custom admin-created rail — items are curated {kind, id} pairs.
              if (rail.custom && Array.isArray(rail.items)) {
                const resolve = (it) => {
                  if (!it) return null;
                  if (it.kind === 'library') return library.find((x) => x.id === it.id);
                  if (it.kind === 'event-video') return eventVideos.find((x) => x.id === it.id);
                  if (it.kind === 'event-photo') return eventPhotos.find((x) => x.id === it.id);
                  if (it.kind === 'teaser') return teasers.find((x) => x.url === it.id);
                  if (it.kind === 'live-teaser') return liveTeasers.find((x) => x.url === it.id);
                  if (it.kind === 'studio-project') return studioProjects.find((x) => x.id === it.id);
                  if (it.kind === 'coach') return coaches.find((x) => x.id === it.id);
                  // Events — kinds 'upcoming' and 'past' both just mean "event".
                  // The admin saved the kind based on where they picked it
                  // from, but by the time it renders here it may live in the
                  // other bucket (past-dated event that was still in the
                  // upcoming admin feed, etc). Check both.
                  if (it.kind === 'upcoming' || it.kind === 'past') {
                    return upcoming.find((x) => x.id === it.id)
                        || past.find((x) => x.id === it.id);
                  }
                  return null;
                };
                const resolved = rail.items
                  .map((it) => ({ item: resolve(it), kind: it.kind }))
                  .filter((x) => !!x.item);
                if (resolved.length === 0) return null;
                // Pin by id if a featured_id matches
                let list = resolved;
                if (rail.featured_id) {
                  const idx = list.findIndex((x) => x.item.id === rail.featured_id);
                  if (idx > 0) {
                    list = list.slice();
                    list.unshift(list.splice(idx, 1)[0]);
                  }
                }
                return (
                  <Rail
                    key={`rail-${rail.key}`}
                    title={rail.label}
                    items={list}
                    elevated={rail.style === 'elevated'}
                    testid={`netflix-rail-${rail.key}`}
                    renderTile={({ item: x, kind }) => {
                      const isFeatured = rail.featured_id && x.id === rail.featured_id;
                      if (kind === 'event-video') {
                        // Clicking an event-video tile deep-links into the
                        // Event Watch page with that specific clip pre-
                        // selected via `?v=`, so the full event playlist is
                        // right there and the admin's clip order is honored.
                        return (
                          <Tile
                            key={`${rail.key}-ev-${x.id}`}
                            thumb={x.thumbnail_url}
                            title={clipLabel(x)}
                            subtitle=""
                            description=""
                            tags={[x.event_title].filter(Boolean)}
                            badge={isFeatured ? 'Featured' : null}
                            discipline="event"
                            kind="Video"
                            href={x.event_id ? `/events/${x.event_id}/watch?v=${x.id}` : undefined}
                          />
                        );
                      }
                      if (kind === 'event-photo') {
                        // Photo tile — click opens the event's gallery.
                        return (
                          <Tile
                            key={`${rail.key}-ph-${x.id}`}
                            thumb={x.thumbnail_url || x.url}
                            title={x.filename || 'Photo'}
                            subtitle={x.event_title || ''}
                            description=""
                            tags={[x.event_title].filter(Boolean)}
                            badge={isFeatured ? 'Featured' : 'Photo'}
                            discipline="event"
                            kind="Photo"
                            href={x.event_id ? `/gallery/${x.event_id}` : '/gallery'}
                          />
                        );
                      }
                      if (kind === 'teaser' || kind === 'live-teaser') {
                        // Teasers are stored as {url, title}. Play inline
                        // via the shared VideoModal (handles mp4/iframe).
                        return (
                          <Tile
                            key={`${rail.key}-${kind}-${x.url}`}
                            thumb={x.thumbnail_url || x.poster_url}
                            title={x.title || (kind === 'teaser' ? 'Teaser' : 'Live Teaser')}
                            subtitle={kind === 'teaser' ? 'Short teaser (9:16)' : 'Live event teaser'}
                            description=""
                            tags={[kind === 'teaser' ? 'Teaser' : 'Live Teaser']}
                            badge={isFeatured ? 'Featured' : 'Teaser'}
                            discipline="general"
                            kind="Video"
                            onPlay={() => openPlayer({ title: x.title || 'Teaser', embed_code: x.url, id: x.url }, { kind: 'teaser' })}
                          />
                        );
                      }
                      if (kind === 'studio-project') {
                        // Video Studio projects — coach-produced edited
                        // videos with annotations. Play the rendered
                        // `final_video_url` inline.
                        const src = x.final_video_url || x.source_video_url;
                        return (
                          <Tile
                            key={`${rail.key}-sp-${x.id}`}
                            thumb={x.thumbnail_url}
                            title={x.title || 'Studio project'}
                            subtitle={x.coach_name ? `by ${x.coach_name}` : 'Video Studio'}
                            description=""
                            tags={[x.coach_name].filter(Boolean)}
                            badge={isFeatured ? 'Featured' : 'Studio'}
                            discipline="general"
                            kind="Video"
                            onPlay={src
                              ? () => openPlayer({ title: x.title || 'Studio project', embed_code: src, id: x.id }, { kind: 'studio-project' })
                              : undefined}
                          />
                        );
                      }
                      if (kind === 'coach') {
                        return (
                          <Tile
                            key={`${rail.key}-coach-${x.id}`}
                            thumb={x.photo_url}
                            title={x.name}
                            subtitle={x.region || ''}
                            description={x.bio || x.tagline || ''}
                            tags={(x.disciplines || []).slice(0, 4)}
                            badge={isFeatured ? 'Featured' : null}
                            portrait
                            noActions
                            discipline="coach"
                            kind="Coach"
                            onPlay={() => openCoachInfo(x)}
                          />
                        );
                      }
                      if (kind === 'upcoming' || kind === 'past') {
                        // Event tiles always navigate to the Netflix-style
                        // watch page where the Opening Video auto-loads.
                        // Honor homepage_* overrides so admins can tailor
                        // what's shown on the home page without touching the
                        // underlying event record.
                        const displayTitle = x.homepage_title || x.title;
                        const displayDesc = x.homepage_description || x.description || '';
                        const displayThumb = x.homepage_thumbnail_url || x.thumbnail_url || x.image_url;
                        const clips = eventVideos.filter((v) => v.event_id === x.id);
                        return (
                          <EventTileWithTray
                            key={`${rail.key}-${kind}-${x.id}`}
                            event={x}
                            clips={clips}
                            openPlayer={openPlayer}
                          >
                            <Tile
                              thumb={displayThumb}
                              title={displayTitle}
                              subtitle={x.event_date || (kind === 'upcoming' ? 'Upcoming' : '')}
                              description={displayDesc}
                              tags={[x.event_type, x.location].filter(Boolean)}
                              badge={isFeatured ? 'Featured' : (x.event_type === 'competition' ? 'Comp' : null)}
                              discipline="event"
                              kind="Event"
                              href={`/events/${x.id}/watch`}
                            />
                          </EventTileWithTray>
                        );
                      }
                      // library
                      return (
                        <Tile
                          key={`${rail.key}-lib-${x.id}`}
                          thumb={fallbackThumb(x)}
                          title={x.title}
                          subtitle={[x.category, x.difficulty].filter(Boolean).join(' · ')}
                          description={x.description || ''}
                          tags={[x.category, x.difficulty].filter(Boolean)}
                          badge={isFeatured ? 'Featured' : (x.difficulty === 'advanced' ? 'Pro' : null)}
                          discipline={(x.category || 'general').toLowerCase()}
                          kind="Video"
                          onPlay={() => openPlayer(x)}
                        />
                      );
                    }}
                  />
                );
              }

              return null;
            })}
          </div>
        </>
      )}

      {/* Footer */}
      <footer className="px-4 sm:px-8 py-10 border-t border-white/10 text-center">
        <p className="text-xs uppercase tracking-widest text-white/40">
          Elevated Roping Media
        </p>
      </footer>

      {/* Playback + auth modals */}
      <VideoModal
        open={!!playing}
        onClose={() => setPlaying(null)}
        title={playing?.title}
        embedCode={playing?.embedCode}
        videoId={playing?.videoId}
        videoKind={playing?.videoKind}
        saved={!!playing?.saveKind && saved.isSaved(playing.saveKind, playing.saveId)}
        onToggleSave={playing?.saveKind && user ? () => saved.toggle(playing.saveKind, playing.saveId) : null}
      />
      <LoginPromptModal
        open={!!loginPrompt}
        onClose={() => setLoginPrompt(null)}
        title={loginPrompt?.title}
        mode={loginPrompt?.mode || 'login'}
        onSubscribe={() => setSubscribeOpen(true)}
      />
      <SubscribePlanModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        isAuthenticated={isAuthed && !!user}
      />
      <InfoModal
        open={!!info}
        onClose={() => setInfo(null)}
        record={info}
      />

      {/* Hide scrollbars inside rails */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default NetflixLandingPage;
